import API from '@services/API';
import { setError, startSpinner, stopSpinner, showModal, hideAllModals } from '../../common/actionCreators';
import { configs } from '@constants/configs/configs';
import { ModalTypes } from '@constants/individualPortal/modals/types';
import { dashboardModalTypes } from '@constants/individualPortal/dashboard/modals/dashboardModalTypes';
import { LOCAL_STORAGE } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { processCommon } from '@utils/dashboard/processCommon';
import {
    DASHBOARD_MODELS_ACTION_TYPES,
    MULTIHOUSEHOLD_ACTION_TYPES,
    TAB_ACTION_TYPES,
    CTA_ACTION_TYPES,
    VERIFY_ACCESS_CODE_TYPE
} from '@constants/actionTypes/dashboardActionTypes';



import { preventerTools } from '@utils/tools/preventerTools';

export const setActiveHouseholdId = householdId => ({
    type: MULTIHOUSEHOLD_ACTION_TYPES.SET_ACTIVE_HOUSEHOLD,
    activeHouseholdId: householdId
});

export const setDashboardModels = dashboardModels => ({
    type: DASHBOARD_MODELS_ACTION_TYPES.SET_DASHBOARD_MODELS,
    dashboardModels
});

export const changeDashboardModels = newState => ({
    type: DASHBOARD_MODELS_ACTION_TYPES.CHANGE_DASHBOARD_MODELS,
    newState
});

export const setActiveTab = activeTabIndex => ({
    type: TAB_ACTION_TYPES.SET_ACTIVE_TAB,
    activeTabIndex
});

export const setCTA = ctaType => ({
    type: CTA_ACTION_TYPES.SET_CTA_TYPE,
    ctaType
});

export const setVerifyAccessCode =  verifyAccessCodeType => ({
    type: VERIFY_ACCESS_CODE_TYPE.SET_VERIFY_ACCESS_CODE_TYPE,
    verifyAccessCodeType
});


/************************

 # Async action creators

 ************************/

const actions = { setError, startSpinner, stopSpinner };

export const fetchDashboardModels = (url, setItem, getItem) => async (dispatch, getState) => {
    const data = await API.getData(url, dispatch, actions, true, getState());
    if (data) {
        dispatch(setDashboardModels(data));
        const activeTabIndex = processCommon.getActiveTabIndex(data, getItem);
        setItem(LOCAL_STORAGE.ACTIVE_TAB_INDEX, activeTabIndex);
        dispatch(setActiveTab(activeTabIndex));
        try{
            processCommon.storeTabData(data, activeTabIndex);
        } catch(e) {
            setItem(LOCAL_STORAGE.ACTIVE_TAB_INDEX, 0);
            dispatch(setActiveTab(0));
            processCommon.storeTabData(data, 0);
        }
    }
};

export const checkActiveEnrollment = (url, applicationYear ) => async dispatch => {
    const data = await API.getData(url, dispatch, actions);

    if(data && data.status === 'success' && data.result) {
        window.location = configs.endpoints.REPORT_A_CHANGE.processReplacements({ applicationYear });
    } else {
        window.location = configs.endpoints.QEP;
    }
};

export const cloneApplication = (url, cb, cbParams) => async dispatch => {
    const data = await API.getData(url, dispatch, actions);

    if(data && data.status === 'success') {
        cb({ ...cbParams, caseNumber: data.caseNumber });
    } else {
        console.error('Dashboard Action Creator: cloneApplication', data.message);
    }
};

export const copyApplication = (url, cb, cbParams) => async dispatch => {
    const data = await API.getData(url, dispatch, actions);

    if (data && data.success) {
        const { encryptedCaseNumber, caseNumber } = data.payload;
        localStorage.setItem('enrollCaseNumber', caseNumber);
        localStorage.setItem('encryptedCaseNumber', encryptedCaseNumber);
        cb(cbParams, encryptedCaseNumber );
    } else {
        console.error('Dashboard Action Creator: copyApplication', data && data.message);
    }
};

export const changeAppStatus = (url, cb) => async dispatch => {
    const data = await API.getData(url, dispatch, actions);

    if (data && data.success) {
        cb();
    } else {
        console.error('Dashboard Action Creator: changeAppStatus', data && data.message);
    }
};

export const getTickets = (url) => async dispatch => {
    const data = await API.getData(url, dispatch, actions);

    if (data) {
        return data;
    } else {
        console.error('Cannot getTickets', url);
    }
};

export const getComments = (...args) => async dispatch => {
    const [url] = args;
    const params = [ url];
    const data = await API.getData(params, dispatch, actions);
    if (data) {
        return data;
    } else {
        console.error('Cannot getComments', url);
    }

};

/************************

 # POST calls

 ************************/

export const verifyAccessCode = (...args) => async dispatch => {

    const [ url, body ] = args;
    const params = [ url, body, undefined, false ];
    const data = await API.postData(params, dispatch, actions);

    const {
        url: redirectUrl
    } = preventerTools.provideObject(data);

    if(redirectUrl) {
        window.location = configs.endpoints.ACCESS_CODE_REDIRECT.processReplacements({ redirectUrl });
    } else {
        // Initial implementation is refactored:
        // 1. There is no need to call stopSpinner since it is not invoked;
        // 2. showModal is wrapped with dispatch to make it appear on UI
        // 3. setVerifyAccessCode is removed since it cannot be passed in modal as prop but only as a part of modalData
        // (current modal implementation doesn't expect any props as part of modalData
        // TODO: reach out this function author to clarify if we use it and make necessary refactor

        dispatch(showModal({
            modalType: ModalTypes.ACCESS_CODE_INVALID_MODAL
        }));
    }
};

export const saveTobaccoChanges = (...args) => async dispatch => {
    const data = await API.postData([ ...args ], dispatch, actions);

    if (data) {
        return data;
    } else {
        console.error('Cannot saveTobaccoChanges');
    }
};


export const cancelApp = (...args) => async dispatch => {
    const [ url, body ] = args;
    const params = [ url, body, undefined, false ];
    const data = await API.postData(params, dispatch, actions);

    if (data && data.result) {
        window.location.reload();
    } else {
        dispatch(hideAllModals());
        dispatch(showModal({
            modalType: dashboardModalTypes.FAILURE_MODAL
        }));
        console.error(data && data.message);
    }
};


export const createTicket = (...args) => async dispatch => {
    const [url, body ] = args;
    const params = [ url, body, undefined, false ];
    const data = await API.postData(params, dispatch, actions);
    if (data) {
        return data;
    } else {
        console.error('Cannot createTickets', url);
    }
};

export const postComment = (...args) => async dispatch => {
    const [url, body ] = args;
    const params = [ url, body ];

    const data = await API.postData(params, dispatch, actions);

    if (data) {
        return data;
    } else {
        console.error('Cannot postComment', url);
    }
};

export const checkPriorSubmittedApp = ( body, cb) => async dispatch => {
    const data = await API.postData([ configs.endpoints.GET_EXISTING_APP_ID, body ], dispatch, actions);
    if (data && data.success) {
        const clonedAppId = data.payload;
        dispatch(hideAllModals());
        dispatch(showModal({
            modalType: dashboardModalTypes.REUSE_PRIOR_APP,
            modalData: { caseNumber: clonedAppId }
        }));
    } else {
        console.error('Cannot checkPriorSubmittedApp', data && data.message);
        cb();
    }
};

export const cloneAndRedirectLCE = (encryptedCaseNumber) => async dispatch => {
    const data = await API.postData([ configs.endpoints.LCE_CLONE.processReplacements({ encryptedCaseNumber })], dispatch, actions);
    if (data && data.success) {
        const encryptedCaseNumber = data.payload.encryptedCaseNumber;
        window.location = configs.endpoints.LCE_REFERRAL.processReplacements({ encryptedCaseNumber });
    } else {
        console.error('Cannot cloneAndRedirectLCE', data && data.message);
    }
};